import { Fragment, memo, useRef, useState } from "react";
import cn from "classnames";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addConditionRunInEntry, reorderSlaveDataBlocks, selectEndpointElem } from "../../store/ses/sesItemConfigSlice";
import { createObjectWithCondition } from "../../helpers";
import useTranslate from "../../hooks/useTranslate";
import { AtomicCondition, IConditionData, IConditionVariable, IEntryConditions } from "../../types/ses/sesItemConfigTypes";
import Condition from "./Condition/Condition";
import DraggablePopoverComponent from "../../ui/DraggablePopoverComponent/DraggablePopoverComponent";
import FormAddingCondition from "../Forms/FormAddingCondition/FormAddingCondition";
import { DragItem, IConditionBlockProps } from "./ConditionBlock.props";
import styles from "./ConditionBlock.module.scss";
import { Hint } from "@/HOC";
import { selectHintsEnabled } from "@/store/hintsSlice";
import ConditionOr from "./ConditionOr/ConditionOr";
import { CustomButton } from "@/ui";
// import { hintsForConditionBlockBlockAnd } from "./ConditionBlock.const";

const ConditionBlock = ({
	conditionDataBlock,
	conditionBlockIdx,
	// arrayConditionsData,
	isDisable,
	changeFlgHandler,
	conditionFor,
	addConditionRunHandler,
	isWarning,
	// showAddCondtionBlockButton,
	orBlockIndex,
	dataElemListOptions,
}: IConditionBlockProps): JSX.Element => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null); // якорь для RUN условий в ENTRY
	const fieldSetRef = useRef<HTMLFieldSetElement>(null); // ссылка на контейнер

	const dispatch = useAppDispatch();
	const endpointElem = useAppSelector(selectEndpointElem); // store - конечная точка
	const hintEnabled = useAppSelector(selectHintsEnabled);
	const translate = useTranslate(); // hook для перевода текста
	const [isConditionReady, setIsConditionReady] = useState(false);

	// обработчик добавления условия RUN в ENTRY
	const addConditionRunInEntryHandler = (conditionRunBlockIdx: number, data?: AtomicCondition | null, orBlockIndex?: number): void => {
		dispatch(
			addConditionRunInEntry({
				conditionEntryBlockIdx: conditionBlockIdx,
				conditionRunBlockIdx: conditionRunBlockIdx,
				data: (data as IConditionData | IConditionVariable) || createObjectWithCondition("data"),
				orBlockIndex,
			})
		); // добавляем условие
		setIsConditionReady(true);
		changeFlgHandler("entry"); // ставим флаг о несохраненных данных
	};

	const [{ isDragging }, drag] = useDrag({
		type: "condition",
		item: () => {
			return { id: conditionBlockIdx, index: conditionBlockIdx };
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
		accept: "condition",
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: DragItem, monitor) {
			if (!fieldSetRef.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = conditionBlockIdx;

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) return;

			// Determine rectangle on screen
			const hoverBoundingRect = fieldSetRef.current?.getBoundingClientRect();

			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

			// логика для перестановки блоков ИЛИ slaveData
			if (conditionFor.for === "dataElemSlaveData") {
				dispatch(
					reorderSlaveDataBlocks({
						fromIdx: dragIndex,
						toIdx: hoverIndex,
					})
				);
				changeFlgHandler("slaveData");

				item.index = hoverIndex;
				return;
			}
			// логика для перестановки блоков ИЛИ runInEntry
			if (conditionFor.for === "endpointRunInEntry") {
				// Time to actually perform the action
				conditionFor.for === "endpointRunInEntry" && conditionFor.moveCondition(dragIndex, hoverIndex, conditionFor.conditionEntryBlockIdx);

				// Note: we're mutating the monitor item here!
				// Generally it's better to avoid mutations,
				// but it's good here for the sake of performance
				// to avoid expensive index searches.
				item.index = hoverIndex;
			}
		},
	});

	["endpointRunInEntry", "dataElemSlaveData"].includes(conditionFor.for) && !isDisable && drag(drop(fieldSetRef));

	return (
		<>
			{/* <Hint hint={translate(hintsForConditionBlockBlockAnd[conditionFor.for])} width="100%" position="right"> */}
			<fieldset
				className={cn(styles.conditionDataBlock, {
					[styles.conditionDataBlockActive]: anchorEl,
					[styles.conditionDataBlockCursorMove]: conditionFor.for === "endpointRunInEntry" || conditionFor.for === "dataElemSlaveData",
					[styles.conditionDataBlockOpacity]: isDragging,
					[styles.conditionDataBlockWarning]: isWarning,
				})}
				ref={fieldSetRef}
				data-handler-id={handlerId}
			>
				<legend>
					<div className={styles.legendBlock}>
						<button type="button" disabled>
							{translate("title_conditionBlockAnd")}
						</button>
						{!isDisable && (
							<button
								type="button"
								onClick={() => addConditionRunHandler(conditionBlockIdx, null)}
								title={hintEnabled ? "" : translate("button_addCondition")}
								className={isConditionReady ? styles.appearance : styles.legendButton}
							>
								<Hint hint={translate("hint_+")}>
									<>+</>
								</Hint>
							</button>
						)}
					</div>
					<Hint hint={translate("hint_runConditions")} position="top">
						<div className={styles.legendBlock}>
							{conditionFor.for === "endpointEntry" && (
								<button type="button" onClick={(e) => setAnchorEl(e.currentTarget)} className={isConditionReady ? styles.appearance : styles.legendButton}>
									{translate("button_runConditions")}
									{endpointElem.endpoint && (endpointElem.endpoint.entry as IEntryConditions[])[conditionBlockIdx]?.run.length > 0 && (
										<> ({(endpointElem.endpoint.entry as IEntryConditions[])[conditionBlockIdx]?.run.length})</>
									)}
								</button>
							)}
						</div>
					</Hint>
				</legend>

				{Array.isArray(conditionDataBlock) &&
					conditionDataBlock?.map((conditionData, conditionIdx) => {
						return conditionData.type !== "or" ? (
							<Fragment
								key={
									conditionData.type +
									(conditionData.type !== "break" && conditionData.type !== "confirmation" ? conditionData.id : "break") +
									conditionBlockIdx +
									conditionIdx +
									(conditionFor.for === "categoryAction" && conditionFor.categoryId)
								}
							>
								{/* блоки И */}
								<Condition
									conditionData={conditionData}
									conditionBlockIdx={conditionBlockIdx}
									conditionIdx={conditionIdx}
									isDisable={isDisable}
									changeFlgHandler={changeFlgHandler}
									conditionFor={conditionFor}
									showDragIcon={(conditionFor.for === "endpointRunInEntry" || conditionFor.for === "dataElemSlaveData") && !isDisable}
									isConditionReady={isConditionReady}
									setIsConditionReady={setIsConditionReady}
									// isThisOrBlock={isThisOrBlock}
									orBlockIndex={orBlockIndex}
									dragIdx={undefined}
									dataElemListOptions={dataElemListOptions}
								/>
							</Fragment>
						) : (
							<ConditionOr
								key={conditionData.type + conditionBlockIdx + conditionIdx + (conditionFor.for === "categoryAction" && conditionFor.categoryId)}
								conditionData={conditionData}
								conditionBlockIdx={conditionBlockIdx}
								conditionIdx={conditionIdx}
								isDisable={isDisable}
								changeFlgHandler={changeFlgHandler}
								conditionFor={conditionFor}
								showDragIcon={(conditionFor.for === "endpointRunInEntry" || conditionFor.for === "dataElemSlaveData") && !isDisable}
								isConditionReady={isConditionReady}
								setIsConditionReady={setIsConditionReady}
								addConditionRunHandler={addConditionRunHandler}
								orBlockIndex={orBlockIndex}
								conditionWithOrOption={true}
								dataElemListOptions={dataElemListOptions}
							/>
						);
					})}
			</fieldset>

			{/* run в entry */}
			{conditionFor.for === "endpointEntry" && (
				<DraggablePopoverComponent
					anchorEl={anchorEl}
					setAnchorEl={setAnchorEl}
					header="title_conditionsRun"
					additionalButtons={
						<>
							{/* сохранить */}
							{conditionFor.isShownSaveButton && conditionFor.isAccessSaveButton && (
								<CustomButton fullWidth disabled={conditionFor.disabledSaveButton} onClick={conditionFor.onClickSaveButtonHandler}>
									{translate("button_save")}
								</CustomButton>
							)}
						</>
					}
					sx={{ minWidth: "500px" }}
				>
					{endpointElem.endpoint &&
						(endpointElem.endpoint.entry as IEntryConditions[])[conditionBlockIdx]?.run?.map(
							(conditionRunDataBlock, conditionRunBlockIdx /* , arrayConditionsData */) => (
								// блоки ИЛИ
								<>
									<ConditionBlock
										conditionDataBlock={conditionRunDataBlock}
										conditionBlockIdx={conditionRunBlockIdx}
										isDisable={isDisable}
										changeFlgHandler={changeFlgHandler}
										conditionFor={{
											for: "endpointRunInEntry",
											conditionEntryBlockIdx: conditionBlockIdx,
											moveCondition: conditionFor.moveCondition,
										}}
										addConditionRunHandler={addConditionRunInEntryHandler}
										// arrayConditionsData={arrayConditionsData}
										key={conditionRunDataBlock.toString() + conditionRunBlockIdx}
										showAddCondtionBlockButton={false} // УБРАТЬ ПОСЛЕ ДОАБОТКИ
										dataElemListOptions={dataElemListOptions}
									/>
								</>
							)
						)}
					{/* форма добавления условия */}
					{!isDisable && (
						<FormAddingCondition
							conditionForFormVariant={endpointElem.endpoint !== null && (endpointElem.endpoint.entry as IEntryConditions[])[conditionBlockIdx]?.run?.length === 0}
							addCondition={(data) =>
								endpointElem.endpoint && addConditionRunInEntryHandler((endpointElem.endpoint.entry as IEntryConditions[])[conditionBlockIdx]?.run?.length || 0, data)
							}
						/>
					)}
				</DraggablePopoverComponent>
			)}
		</>
	);
};

export default memo(ConditionBlock);
